import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Spinner as SpinnerSmall } from "../common/spinner";
import { apiRequestSecuredPost } from "../../lib/common/api";
import { useAuthContext } from "../../contexts/auth-context";
import { TGenericApiResponse } from "../../lib/types/general";
import { TUserInfo } from "../../lib/types/users";

dayjs.extend(utc);
type VerificationStatus = "none" | "waiting" | "checking" | "verified";

const URLS = {
    sendOTP: "users/send-otp",
    verifyPhoneNumber: "users/verify-phone-number",
};
type TOtpInfo = {
    code: number;
    expiration_minutes: number;
    creation_date: Date;
};

export const PhoneVerifier = () => {
    const [status, setStatus] = useState<VerificationStatus>("none");
    const [otp, setOtp] = useState<string>("");
    const [countdown, setCountdown] = useState<number>(300);
    const { getUserTokenId, userInfo: currentUserInfo, updateUserInfoInStorage, setUserInfo: updateUserInfoInContext } = useAuthContext();
    const [errorInfo, setErrorInfo] = useState<string>("");

    const startCountdown = (expirationTime: dayjs.Dayjs) => {
        const remainingSeconds = expirationTime.diff(Date.now(), "seconds");
        setCountdown(remainingSeconds);

        if (remainingSeconds <= 1) {
            setStatus("none");
            return;
        }
        setTimeout(() => {
            startCountdown(expirationTime);
        }, 1000);
    };

    const handleSendOTP = async () => {
        // Here you would call your API to send the OTP
        setErrorInfo("");
        console.log("Sending OTP...");
        const userTokenId = await getUserTokenId();
        try {
            const otpInfo = await apiRequestSecuredPost<TGenericApiResponse<TOtpInfo>>(URLS.sendOTP, userTokenId);
            if (!otpInfo.data || !otpInfo.data.data || !otpInfo.data.data.expiration_minutes || !otpInfo.data.data.creation_date) {
                throw new Error("No OTP received");
            }
            //we get the expiration time from the API
            const expirationTime = dayjs(otpInfo.data.data.creation_date).add(otpInfo.data.data.expiration_minutes, "minutes");

            setStatus("waiting");
            startCountdown(expirationTime);
        } catch (error) {
            console.log("ERROR: ", error);
            setErrorInfo("An error occurred, please try again");
            // setStatus("waiting");
            // const expirationTime = dayjs(Date.now()).add(5, "minutes");
            // startCountdown(expirationTime);
            return;
        }
    };

    const handleVerifyOTP = async () => {
        setStatus("checking");
        setErrorInfo("");
        // Here you would call your API to verify the OTP
        console.log(`Verifying OTP: ${otp}`);
        const userTokenId = await getUserTokenId();
        try {
            const otpInfo = await apiRequestSecuredPost<TGenericApiResponse<boolean>>(URLS.verifyPhoneNumber, userTokenId, { otp });
            if (!otpInfo.data || !otpInfo.data.data) {
                setErrorInfo("Invalid OTP. Please try again.");
                setStatus("waiting");
                setOtp("");
                return;
            }
            // mock verification
            const _tmpUserInfo = { ...currentUserInfo } as TUserInfo;
            _tmpUserInfo.verifications.phone = true;
            updateUserInfoInStorage(_tmpUserInfo);
            updateUserInfoInContext(_tmpUserInfo);
            setStatus("verified");
            setOtp("");
        } catch (error) {
            console.log("ERROR: ", error);
            setErrorInfo("An error occurred, please try again");
            return;
        }
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
    };

    switch (status) {
        case "none":
            return (
                <>
                    <p className="text-white text-start small mb-2">Verify your phone number to enable important notifications from WinWinGo.</p>

                    <ul className="text-white small text-start ps-3 mb-2">
                        <li>Job connection notifications</li>
                        <li>Chat notifications</li>
                        <li>Account verification codes</li>
                    </ul>

                    <p className="text-white small text-start mb-3">
                        By continuing, you agree to receive SMS notifications from WinWinGo. Reply <strong>STOP</strong> anytime to unsubscribe.
                    </p>

                    <button onClick={handleSendOTP} className="btn btn-info w-100 fw-bold mt-1">
                        Send Verification Code
                    </button>
                    {!!errorInfo && <p className="mt-2 text-danger small">{errorInfo}</p>}
                </>
            );
        case "waiting":
        case "checking":
            return (
                <div>
                    <input
                        type="text"
                        inputMode="numeric"
                        is="web-otp"
                        autoComplete="one-time-code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                        maxLength={6}
                        placeholder="Enter 6-digit code"
                        className="form-control"
                        style={{ width: "200px", display: "inline-block", marginRight: "10px", borderRadius: "8px", padding: "6px" }}
                    />
                    <br />
                    <button onClick={handleVerifyOTP} className="btn btn-info fw-bold mt-2">
                        {status === "checking" ? "" : "Verify"}
                        {status === "checking" && <SpinnerSmall />}
                    </button>

                    {!!errorInfo && <p className="mt-2 text-danger small">{errorInfo}</p>}
                    <div className="mt-2 text-danger small">Time remaining: {formatTime(countdown)}</div>
                </div>
            );
        case "verified":
            return (
                <div className="text-success">
                    <i className="fas fa-check-circle"></i> Phone Verified
                </div>
            );
        default:
            return null;
    }
};

/*
{
        "code": "593881",
        "expiration_minutes": 5,
        "creation_date": "2025-11-17T12:42:03.786Z"
    }

*/
